import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Autocomplete,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Checkbox,
    Chip,
    Tooltip,
    Stack,
    FormControlLabel,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ProductSearchAutocomplete } from "../common/ProductSearchAutocomplete";
import { Delete as DeleteIcon, CardGiftcard } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Supplier } from "../../services/procurementService";
import { Product } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";

export interface PurchaseOrderItem {
    product_id: string;
    product?: Product;
    quantity_ordered: number;
    unit_cost: number;
    total_cost: number;
    quality_check_required: boolean;
    requested_batch_info?: string;
    notes?: string;
    // A whole line the supplier gave for free (unit_cost/total_cost forced
    // to 0). Only for a product not already on this purchase.
    is_gift?: boolean;
    // Extra free units of this SAME product bundled with quantity_ordered
    // (e.g. ordered 100, supplier ships 110). Never affects total_cost.
    bonus_quantity?: number;
}

export interface PurchaseOrderFormData {
    reference_number: string;
    payment_terms: string;
    shipping_cost: number;
    discount_amount: number;
    notes: string;
}

// Receiving is keyed by product_id, so two lines sharing a product_id on the
// same purchase would make receiving ambiguous. Both pages block submission
// while this is true — free extra units of a product already on the order
// should use that line's Bonus Qty column instead of a second line.
export const hasDuplicateProducts = (items: PurchaseOrderItem[]): boolean =>
    items.some(
        (item, index) =>
            !!item.product_id &&
            items.some(
                (other, i) => i !== index && other.product_id === item.product_id,
            ),
    );

export const calculatePurchaseOrderTotals = (
    items: PurchaseOrderItem[],
    formData: Pick<PurchaseOrderFormData, "shipping_cost" | "discount_amount">,
) => {
    const subtotal = items.reduce((sum, item) => sum + item.total_cost, 0);
    const shipping = Number(formData.shipping_cost) || 0;
    const discount = Number(formData.discount_amount) || 0;
    const total = subtotal - discount + shipping;

    // Round to 2 decimal places to avoid floating point precision issues
    return {
        subtotal: Math.round(subtotal * 100) / 100,
        shipping: Math.round(shipping * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        total: Math.round(total * 100) / 100,
    };
};

interface PurchaseOrderFormProps {
    suppliers: Supplier[];
    products: Product[];
    selectedSupplier: Supplier | null;
    setSelectedSupplier: (supplier: Supplier | null) => void;
    expectedDeliveryDate: Date | null;
    setExpectedDeliveryDate: (date: Date | null) => void;
    formData: PurchaseOrderFormData;
    setFormData: (data: PurchaseOrderFormData) => void;
    items: PurchaseOrderItem[];
    setItems: (items: PurchaseOrderItem[]) => void;
    mode: "create" | "edit";
}

export const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({
    suppliers,
    products,
    selectedSupplier,
    setSelectedSupplier,
    expectedDeliveryDate,
    setExpectedDeliveryDate,
    formData,
    setFormData,
    items,
    setItems,
    mode,
}) => {
    const [addAsGiftEntry, setAddAsGiftEntry] = useState(false);
    const theme = useTheme();
    // Phones get one card per item — the 7-column table needs ~700px.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Focuses+selects the Quantity field of a newly-added row, since that's
    // almost always the very next thing the user wants to change.
    const quantityInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const prevItemsLengthRef = useRef(items.length);
    useEffect(() => {
        if (items.length > prevItemsLengthRef.current) {
            const newInput = quantityInputRefs.current[items.length - 1];
            newInput?.focus();
            newInput?.select();
        }
        prevItemsLengthRef.current = items.length;
    }, [items.length]);

    // Picking a product from the in-list search row adds it immediately —
    // no separate "Add" click. The row itself resets (via a remount key on
    // items.length) so it's always ready for the next pick.
    const handlePickProduct = (product: Product | null) => {
        if (!product) return;
        const cost = addAsGiftEntry
            ? 0
            : parseFloat(product.cost_price.toString());
        setItems([
            ...items,
            {
                product_id: product.product_id,
                product,
                quantity_ordered: 1,
                unit_cost: cost,
                total_cost: cost,
                is_gift: addAsGiftEntry,
                bonus_quantity: 0,
                quality_check_required: false,
                requested_batch_info: "",
                notes: "",
            },
        ]);
        setAddAsGiftEntry(false);
    };

    const removeItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const updateItem = (
        index: number,
        field: keyof PurchaseOrderItem,
        value: any,
    ) => {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        // If product is selected, update the product info and unit cost —
        // gift rows always stay free, never pick up the product's real cost.
        if (field === "product_id") {
            const product = products.find((p) => p.product_id === value);
            if (product) {
                updatedItems[index].product = product;
                updatedItems[index].unit_cost = updatedItems[index].is_gift
                    ? 0
                    : parseFloat(product.cost_price.toString());
            }
        }

        // Marking a row as a gift forces it free immediately, and clears
        // bonus_quantity — that mechanism is only for a paid line.
        if (field === "is_gift" && value) {
            updatedItems[index].unit_cost = 0;
            updatedItems[index].total_cost = 0;
            updatedItems[index].bonus_quantity = 0;
        }

        // Recalculate total cost with proper decimal precision (2 decimal places).
        // Also runs on product_id/is_gift changes, since both of those can
        // change unit_cost above without the caller separately touching it.
        if (
            field === "quantity_ordered" ||
            field === "unit_cost" ||
            field === "product_id" ||
            field === "is_gift"
        ) {
            const rawTotal = updatedItems[index].is_gift
                ? 0
                : updatedItems[index].quantity_ordered *
                  updatedItems[index].unit_cost;
            updatedItems[index].total_cost = Math.round(rawTotal * 100) / 100;
        }

        setItems(updatedItems);
    };

    const isDuplicateProduct = (index: number, productId: string): boolean =>
        !!productId &&
        items.some((it, i) => i !== index && it.product_id === productId);

    const { subtotal, shipping, discount, total } = calculatePurchaseOrderTotals(
        items,
        formData,
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Supplier Information */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Supplier Information
                        </Typography>
                        <Autocomplete
                            options={suppliers}
                            getOptionLabel={(option) => option.name}
                            value={selectedSupplier}
                            onChange={(_, newValue) =>
                                setSelectedSupplier(newValue)
                            }
                            disabled={mode === "edit"}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Supplier"
                                    required
                                    fullWidth
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...otherProps } = props as any;
                                return (
                                    <Box component="li" key={key} {...otherProps}>
                                        <Box>
                                            <Typography variant="body1">
                                                {option.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {option.contact_person} •{" "}
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            }}
                        />

                        {selectedSupplier && (
                            <Box mt={2}>
                                <Typography variant="body2">
                                    <strong>Contact:</strong>{" "}
                                    {selectedSupplier.contact_person}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Email:</strong>{" "}
                                    {selectedSupplier.email}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Phone:</strong>{" "}
                                    {selectedSupplier.phone}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Address:</strong>{" "}
                                    {selectedSupplier.address}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Purchase Details */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Purchase Details
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                    md: "1fr 1fr 1fr",
                                },
                                gap: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                label="Reference Number"
                                value={formData.reference_number}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        reference_number: e.target.value,
                                    })
                                }
                                placeholder="e.g., REF-2024-001"
                            />
                            <TextField
                                fullWidth
                                label="Payment Terms"
                                value={formData.payment_terms}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        payment_terms: e.target.value,
                                    })
                                }
                                placeholder="e.g., Net 30 days"
                            />
                            <DatePicker
                                label="Expected Delivery Date"
                                value={expectedDeliveryDate}
                                onChange={setExpectedDeliveryDate}
                                sx={{ width: "100%" }}
                            />
                            <TextField
                                fullWidth
                                label="Shipping Cost"
                                type="number"
                                value={formData.shipping_cost}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        shipping_cost:
                                            parseFloat(e.target.value) || 0,
                                    })
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ startAdornment: "৳" }}
                            />
                            <TextField
                                fullWidth
                                label="Discount"
                                type="number"
                                value={formData.discount_amount}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        discount_amount:
                                            parseFloat(e.target.value) || 0,
                                    })
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ startAdornment: "৳" }}
                                helperText="Reduces the amount owed to the supplier"
                            />
                        </Box>
                        <TextField
                            fullWidth
                            label="Notes (Optional)"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                })
                            }
                            multiline
                            rows={2}
                            sx={{ mt: 2 }}
                            placeholder="Additional notes or instructions"
                        />
                    </CardContent>
                </Card>

                {/* Purchase Items */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" mb={2}>
                            Purchase Items
                        </Typography>

                        {isMobile ? (
                            <Stack spacing={1.5}>
                                {items.map((item, index) => {
                                    const isDupe = isDuplicateProduct(index, item.product_id);
                                    return (
                                        <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                            <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                                                <ProductSearchAutocomplete
                                                    value={item.product || null}
                                                    onChange={(newValue) =>
                                                        updateItem(index, "product_id", newValue?.product_id || "")
                                                    }
                                                    label="Product"
                                                    secondaryLine="stock"
                                                    sx={{ flex: 1, minWidth: 0 }}
                                                />
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeItem(index)}
                                                    aria-label="Remove item"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                            {item.product && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.product.name}
                                                    </Typography>
                                                    {item.is_gift && (
                                                        <Chip icon={<CardGiftcard fontSize="small" />} label="GIFT" size="small" color="secondary" />
                                                    )}
                                                </Box>
                                            )}
                                            {isDupe && (
                                                <Typography variant="caption" color="error" display="block">
                                                    Already on this purchase — use Bonus Qty on that line instead.
                                                </Typography>
                                            )}
                                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 1, mt: 1.5 }}>
                                                <TextField
                                                    label="Quantity"
                                                    type="number"
                                                    size="small"
                                                    inputRef={(el) => (quantityInputRefs.current[index] = el)}
                                                    value={item.quantity_ordered}
                                                    onChange={(e) =>
                                                        updateItem(index, "quantity_ordered", parseInt(e.target.value) || 0)
                                                    }
                                                    inputProps={{ min: 1 }}
                                                />
                                                <TextField
                                                    label="Bonus Qty"
                                                    type={item.is_gift ? "text" : "number"}
                                                    size="small"
                                                    disabled={item.is_gift}
                                                    value={item.is_gift ? "—" : item.bonus_quantity || 0}
                                                    onChange={(e) =>
                                                        updateItem(index, "bonus_quantity", parseInt(e.target.value) || 0)
                                                    }
                                                    inputProps={{ min: 0 }}
                                                />
                                                <TextField
                                                    label="Unit Cost"
                                                    type={item.is_gift ? "text" : "number"}
                                                    size="small"
                                                    disabled={item.is_gift}
                                                    value={item.is_gift ? "FREE" : item.unit_cost}
                                                    onChange={(e) =>
                                                        updateItem(index, "unit_cost", parseFloat(e.target.value) || 0)
                                                    }
                                                    inputProps={{ min: 0, step: 0.01 }}
                                                />
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!item.is_gift}
                                                            disabled={isDupe && !item.is_gift}
                                                            onChange={(e) => updateItem(index, "is_gift", e.target.checked)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={<Typography variant="body2">Free gift</Typography>}
                                                />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCurrencyBDT(item.total_cost)}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                                {/* Add Product — picking one adds it straight away, as on desktop. */}
                                <Box>
                                    <ProductSearchAutocomplete
                                        key={items.length}
                                        value={null}
                                        onChange={handlePickProduct}
                                        label="Search and add product"
                                        secondaryLine="stock"
                                        sx={{ width: "100%" }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={addAsGiftEntry}
                                                onChange={(e) => setAddAsGiftEntry(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">Add as a free gift from the supplier</Typography>}
                                    />
                                </Box>
                            </Stack>
                        ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="center">
                                            Quantity
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Extra free units of this same product bundled with the quantity above — never affects Total">
                                                <span>Bonus Qty</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center">
                                            Unit Cost
                                        </TableCell>
                                        <TableCell align="center">
                                            Total
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Whole line is free — for a product not already on this purchase">
                                                <span>Gift?</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* Deliberately NOT sorted here — while
                                        the user is actively adding/editing
                                        items, a row jumping to a new
                                        position mid-edit is disorienting.
                                        Items stay in whatever order they
                                        were added/loaded; edit mode sorts
                                        once up front (see EditPurchaseOrder),
                                        and the read-only View/drawer screens
                                        sort every render since nothing is
                                        "in progress" there. */}
                                    {items.map((item, index) => {
                                        const isDupe = isDuplicateProduct(
                                            index,
                                            item.product_id,
                                        );
                                        return (
                                            <React.Fragment key={index}>
                                            <TableRow
                                                sx={
                                                    item.product
                                                        ? {
                                                              "& > td": {
                                                                  borderBottom: 0,
                                                                  paddingBottom: 0.5,
                                                              },
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <ProductSearchAutocomplete
                                                            value={
                                                                item.product ||
                                                                null
                                                            }
                                                            onChange={(
                                                                newValue,
                                                            ) =>
                                                                updateItem(
                                                                    index,
                                                                    "product_id",
                                                                    newValue?.product_id ||
                                                                        "",
                                                                )
                                                            }
                                                            label="Select product"
                                                            secondaryLine="stock"
                                                            sx={{
                                                                flexGrow: 1,
                                                                minWidth: {
                                                                    xs: "100%",
                                                                    sm: 200,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    {isDupe && (
                                                        <Typography
                                                            variant="caption"
                                                            color="error"
                                                        >
                                                            Already on this
                                                            purchase — use
                                                            Bonus Qty on that
                                                            line instead.
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        inputRef={(el) =>
                                                            (quantityInputRefs.current[
                                                                index
                                                            ] = el)
                                                        }
                                                        value={
                                                            item.quantity_ordered
                                                        }
                                                        onChange={(e) =>
                                                            updateItem(
                                                                index,
                                                                "quantity_ordered",
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                ) || 0,
                                                            )
                                                        }
                                                        inputProps={{
                                                            min: 1,
                                                        }}
                                                        sx={{ width: 80 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {item.is_gift ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            align="center"
                                                        >
                                                            —
                                                        </Typography>
                                                    ) : (
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={
                                                                item.bonus_quantity ||
                                                                0
                                                            }
                                                            onChange={(e) =>
                                                                updateItem(
                                                                    index,
                                                                    "bonus_quantity",
                                                                    parseInt(
                                                                        e
                                                                            .target
                                                                            .value,
                                                                    ) || 0,
                                                                )
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                            }}
                                                            sx={{
                                                                width: 80,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {item.is_gift ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            align="center"
                                                        >
                                                            FREE
                                                        </Typography>
                                                    ) : (
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={
                                                                item.unit_cost
                                                            }
                                                            onChange={(e) =>
                                                                updateItem(
                                                                    index,
                                                                    "unit_cost",
                                                                    parseFloat(
                                                                        e
                                                                            .target
                                                                            .value,
                                                                    ) || 0,
                                                                )
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                                step: 0.01,
                                                            }}
                                                            sx={{
                                                                width: 100,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {formatCurrencyBDT(
                                                            item.total_cost,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip
                                                        title={
                                                            isDupe
                                                                ? "This product is already on the purchase — pick a different product to add as a gift"
                                                                : ""
                                                        }
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                checked={
                                                                    !!item.is_gift
                                                                }
                                                                disabled={
                                                                    isDupe &&
                                                                    !item.is_gift
                                                                }
                                                                onChange={(e) =>
                                                                    updateItem(
                                                                        index,
                                                                        "is_gift",
                                                                        e.target
                                                                            .checked,
                                                                    )
                                                                }
                                                                size="small"
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            removeItem(index)
                                                        }
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            {item.product && (
                                                // Full-width row, spanning every
                                                // column — the item's own cell is
                                                // no wider than the Product
                                                // dropdown, but the full name
                                                // needs more room than that to
                                                // stay readable, and the row has
                                                // plenty of empty space under the
                                                // narrower numeric columns to use.
                                                // The GIFT badge lives here too,
                                                // now that the dropdown itself
                                                // has grown too wide to leave it
                                                // room alongside in its own cell.
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={7}
                                                        sx={{
                                                            pt: 0.5,
                                                            pb: 0.5,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {
                                                                    item.product
                                                                        .name
                                                                }
                                                            </Typography>
                                                            {item.is_gift && (
                                                                <Chip
                                                                    icon={
                                                                        <CardGiftcard fontSize="small" />
                                                                    }
                                                                    label="GIFT"
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* Add Product row — sits right after
                                        the last real item (or alone, when
                                        the list is empty), so adding the
                                        next item happens in the natural
                                        reading position instead of a
                                        separate control above or a
                                        floating button. */}
                                    <TableRow>
                                        <TableCell>
                                            <ProductSearchAutocomplete
                                                key={items.length}
                                                value={null}
                                                onChange={handlePickProduct}
                                                label="Search and add product"
                                                secondaryLine="stock"
                                                sx={{ width: "100%", minWidth: 200 }}
                                            />
                                        </TableCell>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell align="center">
                                            <Tooltip title="Add as a free gift from the supplier (0 cost)">
                                                <Checkbox
                                                    checked={addAsGiftEntry}
                                                    onChange={(e) =>
                                                        setAddAsGiftEntry(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Order Summary */}
                {items.length > 0 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Order Summary
                            </Typography>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography>Subtotal:</Typography>
                                <Typography>
                                    {formatCurrencyBDT(subtotal)}
                                </Typography>
                            </Box>
                            {discount > 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={1}
                                >
                                    <Typography>Discount:</Typography>
                                    <Typography color="success.main">
                                        -{formatCurrencyBDT(discount)}
                                    </Typography>
                                </Box>
                            )}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography>Shipping:</Typography>
                                <Typography>
                                    {formatCurrencyBDT(shipping)}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="h6">
                                    Total (Net Payable):
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {formatCurrencyBDT(total)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </LocalizationProvider>
    );
};
